/**
 * User Preferences Context for JOYOUS Admin Dashboard
 * React context and provider for managing user preferences with local storage persistence
 */

'use client';

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  ReactNode
} from 'react';
import {
  UserPreferences,
  UserPreferencesState,
  UserPreferencesContext,
  PreferenceUpdatePayload,
  DEFAULT_PREFERENCES
} from '@/types/user-preferences';
import {
  PreferencesStorage,
  validatePreferences,
  mergeWithDefaults,
  havePreferencesChanged,
  generatePreferenceChangeEvents
} from '@/lib/preferences-utils';
import { preferencesApi } from '@/services/preferences-api';

// Action types for the reducer
type PreferencesAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_PREFERENCES'; payload: UserPreferences }
  | { type: 'UPDATE_PREFERENCES'; payload: Partial<UserPreferences> }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_LAST_SAVED'; payload: Date | null }
  | { type: 'RESET_TO_DEFAULTS' };

// Initial state
const initialState: UserPreferencesState = {
  preferences: DEFAULT_PREFERENCES,
  isLoading: true,
  isSaving: false,
  lastSaved: null,
  error: null
};

// Reducer function
function preferencesReducer(
  state: UserPreferencesState,
  action: PreferencesAction
): UserPreferencesState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_SAVING':
      return { ...state, isSaving: action.payload };
    case 'SET_PREFERENCES':
      return { ...state, preferences: action.payload, isLoading: false };
    case 'UPDATE_PREFERENCES':
      return {
        ...state,
        preferences: { ...state.preferences, ...action.payload }
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_LAST_SAVED':
      return { ...state, lastSaved: action.payload };
    case 'RESET_TO_DEFAULTS':
      return {
        ...state,
        preferences: DEFAULT_PREFERENCES,
        lastSaved: null
      };
    default:
      return state;
  }
}

// Create the context
const PreferencesContextInstance = createContext<UserPreferencesContext | null>(null);

// Provider props
interface PreferencesProviderProps {
  children: ReactNode;
  storageConfig?: {
    storageKey?: string;
    storageType?: 'localStorage' | 'sessionStorage';
    syncWithServer?: boolean;
  };
  onPreferenceChange?: (key: keyof UserPreferences, oldValue: unknown, newValue: unknown) => void;
}

/**
 * Preferences Provider Component
 */
export function PreferencesProvider({
  children,
  storageConfig,
  onPreferenceChange
}: PreferencesProviderProps) {
  const [state, dispatch] = useReducer(preferencesReducer, initialState);
  const storage = new PreferencesStorage(storageConfig);
  const syncTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  /**
   * Load preferences from storage
   */
  const loadPreferences = useCallback(async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      // Try to load from local storage first
      const storedPreferences = storage.load();

      let preferences: UserPreferences;

      if (storedPreferences) {
        preferences = storedPreferences;
      } else {
        // Try to load from server if configured
        if (storageConfig?.syncWithServer) {
          try {
            const serverPreferences = await preferencesApi.getPreferences();
            preferences = serverPreferences;
            // Save to local storage for offline use
            storage.save(preferences);
          } catch (error) {
            console.warn('Failed to load preferences from server, using defaults:', error);
            preferences = DEFAULT_PREFERENCES;
          }
        } else {
          preferences = DEFAULT_PREFERENCES;
        }
      }

      dispatch({ type: 'SET_PREFERENCES', payload: preferences });
    } catch (error) {
      console.error('Failed to load preferences:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load preferences' });
      dispatch({ type: 'SET_PREFERENCES', payload: DEFAULT_PREFERENCES });
    }
  }, []); // Remove dependencies that cause infinite loops

  /**
   * Save preferences to storage
   */
  const savePreferences = useCallback(async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_SAVING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      // Save to local storage
      storage.save(state.preferences);

      // Sync with server if configured
      if (storageConfig?.syncWithServer) {
        try {
          await preferencesApi.updatePreferences(state.preferences);
        } catch (error) {
          console.warn('Failed to sync preferences with server:', error);
          // Continue with local save even if server sync fails
        }
      }

      dispatch({ type: 'SET_LAST_SAVED', payload: new Date() });
    } catch (error) {
      console.error('Failed to save preferences:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to save preferences' });
      throw error;
    } finally {
      dispatch({ type: 'SET_SAVING', payload: false });
    }
  }, [state.preferences, storage, storageConfig]);

  /**
   * Update a single preference
   */
  const updatePreference = useCallback(async <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ): Promise<void> => {
    const oldValue = state.preferences[key];

    if (oldValue === value) {
      return; // No change needed
    }

    // Validate the new value
    const validation = validatePreferences({ [key]: value });
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    const updatedPreferences = { ...state.preferences, [key]: value };

    // Generate change events
    const events = generatePreferenceChangeEvents(state.preferences, updatedPreferences);

    // Update state
    dispatch({ type: 'UPDATE_PREFERENCES', payload: { [key]: value } });

    // Call change listener if provided
    if (onPreferenceChange) {
      onPreferenceChange(key, oldValue, value);
    }

    // Auto-save with debouncing
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      savePreferences();
    }, 1000); // 1 second debounce
  }, [state.preferences, onPreferenceChange, savePreferences]);

  /**
   * Update multiple preferences
   */
  const updatePreferences = useCallback(async (
    updates: PreferenceUpdatePayload
  ): Promise<void> => {
    if (!havePreferencesChanged(state.preferences, updates)) {
      return; // No changes needed
    }

    // Validate all updates
    const validation = validatePreferences(updates);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    const updatedPreferences = { ...state.preferences, ...validation.validPreferences };

    // Generate change events
    const events = generatePreferenceChangeEvents(state.preferences, updatedPreferences);

    // Update state
    dispatch({ type: 'UPDATE_PREFERENCES', payload: validation.validPreferences });

    // Call change listeners
    if (onPreferenceChange) {
      for (const event of events) {
        onPreferenceChange(event.key, event.oldValue, event.newValue);
      }
    }

    // Auto-save with debouncing
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      savePreferences();
    }, 1000); // 1 second debounce
  }, [state.preferences, onPreferenceChange, savePreferences]);

  /**
   * Reset preferences to defaults
   */
  const resetToDefaults = useCallback(async (): Promise<void> => {
    const updatedPreferences = DEFAULT_PREFERENCES;

    // Generate change events
    const events = generatePreferenceChangeEvents(state.preferences, updatedPreferences);

    // Update state
    dispatch({ type: 'RESET_TO_DEFAULTS' });

    // Call change listeners
    if (onPreferenceChange) {
      for (const event of events) {
        onPreferenceChange(event.key, event.oldValue, event.newValue);
      }
    }

    // Save immediately
    await savePreferences();
  }, [state.preferences, onPreferenceChange, savePreferences]);

  /**
   * Refresh preferences from storage/server
   */
  const refreshPreferences = useCallback(async (): Promise<void> => {
    await loadPreferences();
  }, [loadPreferences]);

  /**
   * Initialize preferences on mount
   */
  useEffect(() => {
    let mounted = true;

    const initializePreferences = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });

        // Try to load from local storage first
        const storedPreferences = storage.load();

        let preferences: UserPreferences;

        if (storedPreferences) {
          preferences = storedPreferences;
        } else {
          // Try to load from server if configured
          if (storageConfig?.syncWithServer) {
            try {
              const serverPreferences = await preferencesApi.getPreferences();
              preferences = serverPreferences;
              // Save to local storage for offline use
              storage.save(preferences);
            } catch (error) {
              console.warn('Failed to load preferences from server, using defaults:', error);
              preferences = DEFAULT_PREFERENCES;
            }
          } else {
            preferences = DEFAULT_PREFERENCES;
          }
        }

        if (mounted) {
          dispatch({ type: 'SET_PREFERENCES', payload: preferences });
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
        if (mounted) {
          dispatch({ type: 'SET_ERROR', payload: 'Failed to load preferences' });
          dispatch({ type: 'SET_PREFERENCES', payload: DEFAULT_PREFERENCES });
        }
      }
    };

    initializePreferences();

    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  // Create context value
  const contextValue: UserPreferencesContext = {
    state,
    preferences: state.preferences,
    updatePreference,
    updatePreferences,
    resetToDefaults,
    loadPreferences,
    savePreferences,
    refreshPreferences
  };

  return (
    <PreferencesContextInstance.Provider value={contextValue}>
      {children}
    </PreferencesContextInstance.Provider>
  );
}

/**
 * Hook to use preferences context
 */
export function usePreferences(): UserPreferencesContext {
  const context = useContext(PreferencesContextInstance);

  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }

  return context;
}

/**
 * Hook to get preferences only (stateless)
 */
export function useUserPreferences(): UserPreferences {
  const { preferences } = usePreferences();
  return preferences;
}

/**
 * Hook to get preference formatter
 */
export function usePreferenceFormatter() {
  const preferences = useUserPreferences();

  return React.useMemo(() => {
    return {
      getLocale: () => preferences.language === 'ar' ? 'ar-SA' : 'en-US',
      isArabicInterface: () => preferences.language === 'ar',
      shouldUseArabicNumerals: () => preferences.numberSystem === 'arabic',
      shouldUseHijriCalendar: () => preferences.dateFormat === 'hijri',
      shouldUseMixedCalendar: () => preferences.dateFormat === 'mixed',
      getNumberFormattingOptions: () => ({
        locale: preferences.language === 'ar' ? 'ar-SA' : 'en-US',
        useArabicNumerals: preferences.numberSystem === 'arabic',
        currency: preferences.currency
      }),
      getDateFormattingOptions: () => ({
        locale: preferences.language === 'ar' ? 'ar-SA' : 'en-US',
        useHijri: preferences.dateFormat === 'hijri',
        useMixed: preferences.dateFormat === 'mixed',
        timezone: preferences.timezone
      })
    };
  }, [preferences]);
}

export default PreferencesProvider;